import { useState } from "react";
import { X, FilePdf, PenNib } from "@phosphor-icons/react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import SignatarioPicker from "@/components/SignatarioPicker";

/**
 * Modal reutilizável que solicita o Assinante do documento e faz o download.
 *
 * Props:
 *  - municipioId: string (obrigatório)
 *  - urlBase: string (obrigatório) — url do PDF SEM query, ex.: `/contracts/${id}/pesquisa-precos-consolidado`
 *  - filename: string — nome do arquivo baixado
 *  - title: string — título do modal
 *  - subtitle: string — subtítulo (contrato/aditivo/pesquisa referente)
 *  - onClose: () => void
 *  - onSuccess: () => void (opcional)
 */
export default function SignatarioDownloadModal({ municipioId, urlBase, filename, title, subtitle, onClose, onSuccess }) {
  const [signatarioId, setSignatarioId] = useState("");
  const [downloading, setDownloading] = useState(false);

  const baixar = async () => {
    if (!signatarioId) {
      toast.error("Selecione o assinante do documento");
      return;
    }
    setDownloading(true);
    try {
      const sep = urlBase.includes("?") ? "&" : "?";
      const url = `${API}${urlBase}${sep}signatario_id=${encodeURIComponent(signatarioId)}`;
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl; link.download = filename; link.click();
      window.URL.revokeObjectURL(objectUrl);
      toast.success("PDF baixado com assinante registrado");
      onSuccess && onSuccess();
      onClose();
    } catch {
      toast.error("Erro ao gerar PDF");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[110] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} translate="no">
      <div className="tce-card w-full max-w-lg" onClick={(e) => e.stopPropagation()} translate="no">
        <div className="flex items-center justify-between px-6 py-5 border-b border-[#334155]">
          <div>
            <div className="uppercase-label flex items-center gap-1.5">
              <PenNib size={12} weight="duotone" className="text-[#F59E0B]" />
              Assinatura do documento
            </div>
            <div className="font-heading text-lg mt-1">{title}</div>
            {subtitle && <div className="text-xs text-[#94A3B8] mt-0.5">{subtitle}</div>}
          </div>
          <button onClick={onClose} className="p-2 rounded hover:bg-[#334155] text-[#94A3B8]" data-testid="sig-dl-close">
            <X size={18} />
          </button>
        </div>
        <div className="p-6 space-y-5">
          <SignatarioPicker
            municipioId={municipioId}
            value={signatarioId}
            onChange={setSignatarioId}
            testid="sig-dl-picker"
            required
          />
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose}
                    className="px-4 py-2 rounded-md border border-[#334155] hover:border-[#94A3B8] text-sm">
              Cancelar
            </button>
            <button
              data-testid="sig-dl-confirm"
              disabled={!signatarioId || downloading}
              onClick={baixar}
              className="px-5 py-2 rounded-md bg-[#F59E0B] text-[#0F172A] hover:bg-[#FCD34D] font-medium text-sm disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2">
              <FilePdf size={14} weight="bold" /> <span>{downloading ? "Baixando…" : "Baixar PDF"}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
